import { ChevronDownIcon } from "@src/assets";
import { TokenLogo, MaxBalance } from "@src/components";
import { CASH_TOKEN_ADDRESS_BY_CHAIN } from "@src/config";
import { usePrivySmartWallet, useSwapContext } from "@src/contexts";
import { RoutePath } from "@src/interfaces";
import { TokenWithChain } from "@src/models";
import { useNavigate } from "react-router-dom";

export enum PickerType {
  Cash = "Cash",
  Transfer = "Transfer",
}

type Props = {
  token?: TokenWithChain;
  type?: "source" | "destination";
  pickerType?: PickerType;
  showChain?: boolean;
  onMaxClick?: (balance: string) => void;
  isTokenFixed?: boolean;
};

export const TokenPanel = ({
  token,
  pickerType,
  showChain = false,
  type,
  onMaxClick,
  isTokenFixed = false,
}: Props) => {
  const navigate = useNavigate();
  const { chain, isFetchingBalances, isExternalMode } = useSwapContext();
  const { smartWalletAddress } = usePrivySmartWallet();

  return (
    <div className="flex flex-col items-end">
      <button
        type="button"
        // NOTE: used in e2e tests — do not remove
        data-testid="token-picker-button"
        aria-label="Open token picker"
        onClick={() => {
          if (isTokenFixed) {
            return;
          }

          switch (pickerType) {
            case PickerType.Cash: {
              navigate(RoutePath.PickUsdcChain, {
                state: {
                  chainId: token?.chainId,
                  type,
                },
              });

              return;
            }

            case PickerType.Transfer: {
              navigate(RoutePath.PickToken, {
                state: {
                  tokenAddress:
                    token?.address ||
                    (chain && CASH_TOKEN_ADDRESS_BY_CHAIN[chain.chainId]),
                  excludeZeroBalanceTokens: true,
                },
              });

              return;
            }

            default: {
              navigate(RoutePath.PickToken, {
                state: {
                  tokenAddress: token?.address || "",
                  excludeCash: isExternalMode ? false : true,
                },
              });
            }
          }
        }}
        className={`overflow-hidden ${isTokenFixed ? "cursor-default" : ""}`}
      >
        <div className="flex items-center py-1 gap-2">
          <div className="flex gap-1 items-center">
            {token && (
              <TokenLogo
                token={token}
                tokenLogoClassName="w-6 min-w-6 h-6"
                unknownTokenLogoClassName="w-6 min-w-6 h-6 text-sm"
                withChain={showChain}
                chainLogoClassName="!w-3 !h-3"
                chain={chain}
              />
            )}
            <div className="flex gap-1 flex-col font-medium items-start">
              <p
                className={`text-2xl leading-[100%] ${
                  token ? "" : "opacity-60"
                }`}
              >
                {token ? token.symbol : "Select"}
              </p>
            </div>
          </div>
          {!isTokenFixed && <ChevronDownIcon />}
        </div>
      </button>
      {onMaxClick && (
        <MaxBalance
          token={token}
          onMaxClick={onMaxClick}
          isFetchingBalances={isFetchingBalances}
          smartWalletAddress={smartWalletAddress}
        />
      )}
    </div>
  );
};
